import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    FileBarChart, Loader2, FileDown, Sheet, PenSquare, Send, Plus, CheckCircle2,
    Mail,
} from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";

export default function ReportesTab({ facility }) {
    const { hasRole } = useAuth();
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        periodo: new Date().toISOString().slice(0, 7),
        idioma: "en",
    });

    const canGenerate = hasRole([
        ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.FINANZAS, ROLES.OPERACIONES, ROLES.ADMIN_TI,
    ]);
    const canSign = hasRole([ROLES.DIRECCION, ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.ADMIN_TI]);

    const load = async () => {
        const { data } = await api.get(`/facilities/${facility.id}/reportes`);
        setRows(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    const generar = async () => {
        if (!form.periodo || !/^\d{4}-\d{2}$/.test(form.periodo)) {
            return toast.error("Periodo formato YYYY-MM");
        }
        setBusy(true);
        try {
            await api.post(`/facilities/${facility.id}/reportes/generar`, form);
            toast.success(`Reporte ${form.periodo} generado (DRAFT)`);
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const download = async (id, kind, periodo) => {
        try {
            const r = await api.get(`/reportes/${id}/${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `reporte_${facility.numero}_${periodo}.${kind}`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Error descargando");
        }
    };

    const firmar = async (id) => {
        setBusy(true);
        try {
            await api.post(`/reportes/${id}/firmar`);
            toast.success("Reporte firmado (mock)");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const enviar = async (id) => {
        setBusy(true);
        try {
            await api.post(`/reportes/${id}/enviar`);
            toast.success("Reporte marcado como enviado al lender");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const enviarDigest = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/facilities/${facility.id}/digest-semanal/generar`,
            );
            const count = data?.emails_enviados?.length || 0;
            toast.success(`Digest semanal enviado (${count} email${count === 1 ? "" : "s"} MOCK)`, {
                description: data?.subject,
            });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error enviando digest");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="tab-reportes-content">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-2xl flex items-center gap-2">
                        <FileBarChart className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                        Reportes a lender
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Cada reporte incluye Compliance Certificate, BB Certificate, DPD Roll-forward
                        y Top-10 deudores. PDF con watermark DRAFT hasta que Dirección firme (mock).
                        Excel para revisión detallada.
                    </p>
                </div>
                {canGenerate && (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={enviarDigest} disabled={busy}
                            data-testid="enviar-digest-btn">
                            <Mail className="h-4 w-4 mr-1" /> Enviar digest ahora
                        </Button>
                        <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="generar-reporte-btn">
                                <Plus className="h-4 w-4 mr-1" /> Generar reporte
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Generar reporte mensual</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div><Label>Periodo (YYYY-MM)</Label>
                                    <Input value={form.periodo}
                                        onChange={(e) => setForm({ ...form, periodo: e.target.value })}
                                        data-testid="reporte-periodo" />
                                </div>
                                <div><Label>Idioma</Label>
                                    <Select value={form.idioma}
                                        onValueChange={(v) => setForm({ ...form, idioma: v })}>
                                        <SelectTrigger data-testid="reporte-idioma"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="en">Inglés (default)</SelectItem>
                                            <SelectItem value="es">Español</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="text-[11px] text-muted-foreground bg-muted p-2 rounded-sm">
                                    Se toman las últimas mediciones activas: BB actual, covenants, top-10 deudores.
                                </div>
                            </div>
                            <DialogFooter>
                                <Button onClick={generar} disabled={busy} data-testid="submit-reporte">
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generar"}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                        </Dialog>
                    </div>
                )}
            </div>

            <div className="rounded-md border border-border bg-card overflow-hidden">
                <table className="trf-table" data-testid="reportes-table">
                    <thead>
                        <tr>
                            <th>Periodo</th><th>Idioma</th><th>Generado</th>
                            <th>Firmado</th><th>Enviado</th><th>Resumen</th>
                            <th>Descargas</th><th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} data-testid={`reporte-row-${r.periodo}`}>
                                <td className="font-medium">{r.periodo}</td>
                                <td><span className="badge-gold">{r.idioma}</span></td>
                                <td className="tabular text-xs">
                                    {r.generado_at?.slice(0, 16)}<br/>
                                    <span className="text-[10px] text-muted-foreground">{r.generado_por}</span>
                                </td>
                                <td>
                                    {r.firmado ? (
                                        <span className="badge-exito">
                                            <CheckCircle2 className="h-3 w-3" /> {r.firmado_por?.split("@")[0]}
                                        </span>
                                    ) : (
                                        <span className="badge-mock">DRAFT</span>
                                    )}
                                </td>
                                <td>
                                    {r.enviado ? (
                                        <span className="badge-exito">Sí</span>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                </td>
                                <td className="text-xs">
                                    {r.resumen && (
                                        <div>
                                            {r.resumen.covenants_medidos} covenants · {r.resumen.breaches} breaches
                                        </div>
                                    )}
                                </td>
                                <td className="flex gap-1">
                                    <Button size="sm" variant="ghost"
                                        onClick={() => download(r.id, "pdf", r.periodo)}
                                        data-testid={`dl-pdf-${r.periodo}`}>
                                        <FileDown className="h-3 w-3 mr-1" /> PDF
                                    </Button>
                                    <Button size="sm" variant="ghost"
                                        onClick={() => download(r.id, "xlsx", r.periodo)}
                                        data-testid={`dl-xlsx-${r.periodo}`}>
                                        <Sheet className="h-3 w-3 mr-1" /> XLSX
                                    </Button>
                                </td>
                                <td className="flex gap-1">
                                    {canSign && !r.firmado && (
                                        <Button size="sm" variant="outline"
                                            onClick={() => firmar(r.id)} disabled={busy}
                                            data-testid={`firmar-${r.periodo}`}>
                                            <PenSquare className="h-3 w-3 mr-1" /> Firmar
                                        </Button>
                                    )}
                                    {canSign && r.firmado && !r.enviado && (
                                        <Button size="sm" variant="outline"
                                            onClick={() => enviar(r.id)} disabled={busy}
                                            data-testid={`enviar-${r.periodo}`}>
                                            <Send className="h-3 w-3 mr-1" /> Enviar
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan={8} className="text-center py-6 text-muted-foreground">
                                Sin reportes generados.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
